import React, { useState } from 'react';
import { PastClient } from '@/types';
import { mockPastClients } from '@/data/mockData';
import { formatCurrency, formatPercentage, cn } from '@/utils';

interface PastClientsProps {
  selectedChannel: string;
}

export const PastClients: React.FC<PastClientsProps> = ({ selectedChannel: _selectedChannel }) => {
  const [sortBy, setSortBy] = useState<'savings' | 'score' | 'rate_diff' | 'last_contact'>('savings');
  const [filterMinSavings, setFilterMinSavings] = useState(300);
  const [filterMinScore, setFilterMinScore] = useState(80);

  // Mock portfolio data
  const portfolioStats = {
    totalClients: 4672,
    refiEligible: 2341,
    opportunities: 847,
    contacted: 234,
    monthlyGrowth: 47,
    rateEligible: 1894,
    cashOutEligible: 447,
    avgSavings: 387,
  };

  // Mock market conditions
  const marketConditions = {
    conventional30yr: 3.25,
    fha30yr: 3.125,
    va30yr: 3.0,
    trend: 'falling',
    forecast: 'Expected to drop another 0.1-0.2% this month',
    weeklyChange: -0.125,
  };

  // Extended mock data for opportunities
  const allOpportunities: (PastClient & { score?: number })[] = [
    {
      ...mockPastClients[0],
      score: 94,
    },
    {
      ...mockPastClients[1],
      score: 91,
    },
    {
      id: 'client_3',
      firstName: 'David',
      lastName: 'Rodriguez',
      email: 'david.rodriguez@email.com',
      phone: '(555) 345-6789',
      channel: 'RETAIL',
      originalLoan: {
        date: '2023-06-10',
        amount: 325000,
        rate: 4.25,
        term: 30,
        monthlyPayment: 1598.23,
      },
      refiOpportunity: {
        newRate: 3.625,
        monthlySavings: 234,
        totalSavings: 84240,
        breakEvenMonths: 20,
        urgency: 'medium',
        contactStatus: 'not_contacted',
      },
      engagementHistory: [],
      score: 78,
    },
  ];

  const sortedOpportunities = [...allOpportunities].sort((a, b) => {
    switch (sortBy) {
      case 'savings':
        return (b.refiOpportunity?.monthlySavings || 0) - (a.refiOpportunity?.monthlySavings || 0);
      case 'score':
        return (b.score || 0) - (a.score || 0);
      case 'rate_diff':
        return (b.originalLoan.rate - (b.refiOpportunity?.newRate || 0)) - 
               (a.originalLoan.rate - (a.refiOpportunity?.newRate || 0));
      default:
        return 0;
    }
  });

  // const getUrgencyColor = (urgency: string) => {
  //   switch (urgency) {
  //     case 'high':
  //       return 'bg-red-500/20 text-red-300 border-red-500/30';
  //     case 'medium':
  //       return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  //     case 'low':
  //       return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
  //     default:
  //       return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  //   }
  // };

  const getContactStatusIcon = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return '⏳';
      case 'contacted':
        return '📧';
      case 'engaged':
        return '🎯';
      case 'declined':
        return '❌';
      default:
        return '📋';
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Portfolio Overview */}
      <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-50">
            Past Client Portfolio
          </h2>
          <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-500">
            Sync from Rock
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-50 mb-1">
              {portfolioStats.totalClients.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400 mb-1">Total Clients</div>
            <div className="text-xs text-emerald-400">
              ↗️ +{portfolioStats.monthlyGrowth} this month
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-slate-50 mb-1">
              {portfolioStats.refiEligible.toLocaleString()}
            </div>
            <div className="text-sm text-slate-400 mb-1">Refi Eligible</div>
            <div className="text-xs text-slate-300">
              📊 Rate-driven: {portfolioStats.rateEligible.toLocaleString()} (81%)
            </div>
            <div className="text-xs text-slate-300">
              💰 Cash-out: {portfolioStats.cashOutEligible.toLocaleString()} (19%)
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-slate-50 mb-1">
              {portfolioStats.opportunities}
            </div>
            <div className="text-sm text-slate-400 mb-1">Opportunities</div>
            <div className="text-xs text-emerald-400">
              ↗️ +23 today
            </div>
            <div className="text-xs text-slate-300">
              💰 $2.8M volume potential
            </div>
            <div className="text-xs text-slate-300">
              ⭐ Avg save: ${portfolioStats.avgSavings}/month
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-slate-50 mb-1">
              {portfolioStats.contacted}
            </div>
            <div className="text-sm text-slate-400 mb-1">Contacted</div>
            <div className="text-xs text-slate-300">
              👥 Queue Ready: 89 leads
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Opportunity Queue */}
        <div className="lg:col-span-2 bg-slate-700 rounded-lg border border-slate-600 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">
              Refi Opportunities
            </h2>
            <div className="flex space-x-2">
              <button className="px-3 py-1 text-sm bg-slate-600 text-slate-300 rounded hover:bg-slate-500">
                🔄 Refresh
              </button>
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-500">
                Export
              </button>
            </div>
          </div>

          {/* Sort and Filter Controls */}
          <div className="mb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-400">📊 Sort by:</span>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="bg-slate-800 border border-slate-600 text-slate-300 text-sm rounded px-2 py-1"
                >
                  <option value="savings">Savings ▼</option>
                  <option value="score">Score</option>
                  <option value="rate_diff">Rate Diff</option>
                  <option value="last_contact">Last Contact</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-400">🎯 Filter:</span>
                <button 
                  className={cn(
                    'px-2 py-1 text-xs rounded',
                    filterMinSavings === 300 ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-300'
                  )}
                  onClick={() => setFilterMinSavings(300)}
                >
                  &gt;$300 savings
                </button>
                <button 
                  className={cn(
                    'px-2 py-1 text-xs rounded',
                    filterMinScore === 80 ? 'bg-blue-600 text-white' : 'bg-slate-600 text-slate-300'
                  )}
                  onClick={() => setFilterMinScore(80)}
                >
                  Score &gt;80
                </button>
                <button className="px-2 py-1 text-xs bg-slate-600 text-slate-300 rounded">
                  No recent contact
                </button>
              </div>
            </div>
          </div>

          {/* Opportunities Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-600">
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">Client Name</th>
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">Current Rate</th>
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">New Rate</th>
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">Savings</th>
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">Score</th>
                  <th className="text-left text-sm font-medium text-slate-300 pb-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedOpportunities.map((client, index) => (
                  <tr 
                    key={client.id}
                    className={cn(
                      'border-b border-slate-600/50 hover:bg-slate-600/30',
                      index % 2 === 0 ? 'bg-slate-700/50' : 'bg-slate-800/30'
                    )}
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center space-x-2">
                        {client.refiOpportunity?.urgency === 'high' && (
                          <span className="text-red-400">⭐</span>
                        )}
                        <div>
                          <div className="text-sm font-medium text-slate-200">
                            {client.firstName} {client.lastName}
                          </div>
                          <div className="text-xs text-slate-400">{client.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-slate-300">
                        {formatPercentage(client.originalLoan.rate)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-slate-300">
                        {formatPercentage(client.refiOpportunity?.newRate || 0)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-sm text-emerald-400 font-medium">
                        {formatCurrency(client.refiOpportunity?.monthlySavings || 0)}/mo
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={cn(
                        'text-sm font-medium',
                        (client.score || 0) >= 90 ? 'text-emerald-400' :
                        (client.score || 0) >= 80 ? 'text-amber-400' : 'text-slate-400'
                      )}>
                        {client.score}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center space-x-1">
                        <span>{getContactStatusIcon(client.refiOpportunity?.contactStatus || 'not_contacted')}</span>
                        <button className="text-sm text-blue-400 hover:text-blue-300">
                          Contact
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Market Conditions Panel */}
        <div className="bg-slate-700 rounded-lg border border-slate-600 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">
              Market Intelligence
            </h2>
            <button className="px-3 py-1 text-sm bg-slate-600 text-slate-300 rounded hover:bg-slate-500">
              Update Settings
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-2">
                Current Rates (30-year):
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">• Conventional:</span>
                  <span className="text-slate-300">{formatPercentage(marketConditions.conventional30yr)} 
                    <span className="text-emerald-400 ml-1">(↓ 0.125% this week)</span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">• FHA:</span>
                  <span className="text-slate-300">{formatPercentage(marketConditions.fha30yr)}
                    <span className="text-emerald-400 ml-1">(↓ 0.1% this week)</span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">• VA:</span>
                  <span className="text-slate-300">{formatPercentage(marketConditions.va30yr)}
                    <span className="text-emerald-400 ml-1">(↓ 0.125% this week)</span>
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm text-slate-300 mb-1">
                Rate Trend: <span className="text-emerald-400">📉 Falling (3 weeks)</span>
              </div>
              <div className="text-sm text-slate-400">
                Forecast: {marketConditions.forecast}
              </div>
            </div>

            <div className="border-t border-slate-600 pt-4">
              <h3 className="text-sm font-medium text-slate-300 mb-2">
                Opportunity Triggers:
              </h3>
              <div className="space-y-1 text-sm text-slate-400">
                <div className="flex items-center space-x-2">
                  <span className="text-emerald-400">✅</span>
                  <span>Rate drop &gt;0.5% from client's current rate</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-emerald-400">✅</span>
                  <span>Client's loan balance &gt;$200K (min savings threshold)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-emerald-400">✅</span>
                  <span>No contact in past 90 days</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-emerald-400">✅</span>
                  <span>Market conditions favorable (rates falling/stable)</span>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-600 pt-4">
              <div className="text-sm text-slate-300 mb-1">
                Next Rate Check: Tomorrow 9:00 AM
              </div>
              <div className="text-sm text-emerald-400">
                Auto-Generated Opportunities: 23 ready for outreach
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Client Detail Modal would go here - for now just show a sample */}
      {/* This would be triggered by clicking on a client name */}
    </div>
  );
};